import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { Plus, Edit, Trash2, Hash, Activity, Search, AlertTriangle, CheckCircle, Link, Unlink, ChevronDown, ChevronUp } from 'lucide-react';
import { getChannels, createChannel, updateChannel, deleteChannel, bindTokenToChannel, unbindTokenFromChannel, autoBindTokenToChannel } from '../services/channels';
import { getTokens } from '../services/tokens';

const Channels = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showBindModal, setShowBindModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [channels, setChannels] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [formData, setFormData] = useState({
    channel_id: '',
    is_active: true,
    description: ''
  });
  const [bindFormData, setBindFormData] = useState({
    token_id: '',
    priority: 10
  });
  const [submitting, setSubmitting] = useState(false);
  const [binding, setBinding] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [unbindingTokens, setUnbindingTokens] = useState(new Set());

const fetchChannels = async () => {
  try {
    setLoading(true);
    const response = await getChannels();
    
    setChannels(response);
  } catch (error) {
    console.error('Failed to fetch channels:', error);
    showToast('Failed to load channels: ' + (error.message || error), 'danger');
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  const debugAPI = async () => {
    try {
      const response = await getChannels();
      console.log('API Response:', response);
      if (response.length > 0) {
        console.log('First channel bindings:', response[0].token_bindings);
      }
    } catch (error) {
      console.error('Debug error:', error);
    }
  };
  debugAPI();
}, []);

  // Fetch tokens for binding
  const fetchTokensForBinding = async () => {
    try {
      setLoadingTokens(true);
      const response = await getTokens({ is_active: true, status: 'healthy' });
      setTokens(response);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      showToast('Failed to load tokens: ' + (error.message || error), 'danger');
    } finally {
      setLoadingTokens(false);
    }
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const getStatusVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  const toggleRowExpansion = (channelId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(channelId)) {
      newExpandedRows.delete(channelId);
    } else {
      newExpandedRows.add(channelId);
    }
    setExpandedRows(newExpandedRows);
  };

  const filteredChannels = channels.filter(channel => {
    const matchesSearch = channel.channel_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (channel.description && channel.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && channel.is_active) ||
                         (filterStatus === 'inactive' && !channel.is_active);
    return matchesSearch && matchesFilter;
  });

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setFormData({
      channel_id: channel.channel_id,
      is_active: channel.is_active,
      description: channel.description || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingChannel(null);
    setFormData({
      channel_id: '',
      is_active: true,
      description: ''
    });
    setShowModal(true);
  };

  const handleBindClick = (channel) => {
    setSelectedChannel(channel);
    fetchTokensForBinding();
    setShowBindModal(true);
  };

  const handleAutoBindClick = async (channel) => {
    try {
      setBinding(true);
      await autoBindTokenToChannel(channel.channel_id);
      showToast('Token automatically bound to channel successfully');
      fetchChannels();
    } catch (error) {
      console.error('Failed to auto-bind token:', error);
      showToast('Failed to auto-bind token: ' + (error.message || error), 'danger');
    } finally {
      setBinding(false);
    }
  };

  const handleDeleteClick = (channel) => {
    setChannelToDelete(channel);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (channelToDelete) {
      try {
        await deleteChannel(channelToDelete.channel_id);
        fetchChannels();
        showToast('Channel deleted successfully');
      } catch (error) {
        console.error('Failed to delete channel:', error);
        showToast('Failed to delete channel: ' + (error.message || error), 'danger');
      } finally {
        setShowDeleteModal(false);
        setChannelToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setChannelToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingChannel) {
        await updateChannel(editingChannel.channel_id, formData);
        showToast('Channel updated successfully');
      } else {
        await createChannel(formData);
        showToast('Channel created successfully');
      }
      setShowModal(false);
      fetchChannels();
    } catch (error) {
      console.error('Failed to save channel:', error);
      showToast('Failed to save channel: ' + (error.message || error), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBindSubmit = async (e) => {
    e.preventDefault();
    setBinding(true);

    try {
      await bindTokenToChannel(selectedChannel.channel_id, bindFormData.token_id, bindFormData.priority);
      showToast('Token bound to channel successfully');
      setShowBindModal(false);
      fetchChannels();
    } catch (error) {
      console.error('Failed to bind token:', error);
      showToast('Failed to bind token: ' + (error.message || error), 'danger');
    } finally {
      setBinding(false);
    }
  };

  const handleUnbindClick = async (channelId, tokenId) => {
  try {
    setUnbindingTokens(prev => new Set(prev).add(tokenId));
    await unbindTokenFromChannel(channelId, tokenId);
    showToast('Token unbound from channel successfully');
    fetchChannels();
  } catch (error) {
    console.error('Failed to unbind token:', error);
    showToast('Failed to unbind token: ' + (error.message || error), 'danger');
  } finally {
    setUnbindingTokens(prev => {
      const newSet = new Set(prev);
      newSet.delete(tokenId);
      return newSet;
    });
  }
};

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBindInputChange = (field, value) => {
    setBindFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading channels...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Channel Management</h1>
          <p className="text-muted mb-0">
            Manage Discord channels and token bindings
          </p>
        </div>
        <Button variant="primary" onClick={handleAdd}>
          <Plus size={16} className="me-2" />
          <span className="d-none d-md-inline">Add Channel</span>
          <span className="d-md-none">Add</span>
        </Button>
      </div>

      {/* Info Alert */}
      <Alert variant="info" className="mb-4">
        <AlertTriangle size={20} className="me-2" />
        Channels are where reactions will be performed. Bind tokens to channels to enable reactions.
      </Alert>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Hash size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{channels.length}</h5>
              <p className="text-muted mb-0 small">Total Channels</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Activity size={24} className="text-success mb-2" />
              <h5 className="fw-bold mb-0">{channels.filter(c => c.is_active).length}</h5>
              <p className="text-muted mb-0 small">Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Link size={24} className="text-warning mb-2" />
              <h5 className="fw-bold mb-0">{channels.filter(c => c.token_bindings && c.token_bindings.length > 0).length}</h5>
              <p className="text-muted mb-0 small">With Tokens</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-info mb-2" style={{ fontSize: '1.2rem' }}>🔗</div>
              <h5 className="fw-bold mb-0">{channels.reduce((sum, c) => sum + (c.token_bindings ? c.token_bindings.length : 0), 0)}</h5>
              <p className="text-muted mb-0 small">Total Bindings</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-2">
            <Col xs={12} md={6}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search channels..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <div className="text-muted small d-flex align-items-center h-100 justify-content-end justify-content-md-start">
                {filteredChannels.length} channels
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Channels Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {/* Desktop Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 py-3 px-4">Channel ID</th>
                  <th className="border-0 py-3">Description</th>
                  <th className="border-0 py-3">Status</th>
                  <th className="border-0 py-3">Token Bindings</th>
                  <th className="border-0 py-3">Last Updated</th>
                  <th className="border-0 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredChannels.map((channel) => (
                  <tr key={channel.channel_id} style={{ cursor: 'pointer' }}>
                    <td className="px-4 py-3" onClick={() => toggleRowExpansion(channel.channel_id)}>
                      <div className="fw-medium font-monospace">{channel.channel_id}</div>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(channel.channel_id)}>
                      <div className="text-muted small">{channel.description || 'No description'}</div>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(channel.channel_id)}>
                      <Badge bg={getStatusVariant(channel.is_active)}>
                        {channel.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(channel.channel_id)}>
                      <div className="d-flex flex-column gap-1">
                        {channel.token_bindings && channel.token_bindings.length > 0 ? (
                          channel.token_bindings.map((binding, index) => (
                            <div key={index} className="d-flex align-items-center gap-2">
                              <Badge bg="primary" className="small">
                                Token #{binding.token_id}
                              </Badge>
                              <Badge bg="secondary" className="small">
                                Prio: {binding.priority}
                              </Badge>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnbindClick(channel.channel_id, binding.token_id);
                                }}
                                disabled={unbindingTokens.has(binding.token_id)}
                              >
                                <Unlink size={12} />
                              </Button>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted small">No tokens bound</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3" onClick={() => toggleRowExpansion(channel.channel_id)}>
                      <small className="text-muted">{formatDate(channel.updated_at)}</small>
                    </td>
                    <td className="py-3">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(channel)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleBindClick(channel)}
                        >
                          <Link size={14} />
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleAutoBindClick(channel)}
                          disabled={binding}
                        >
                          <Activity size={14} />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteClick(channel)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="d-md-none">
            {filteredChannels.map((channel) => {
              const isExpanded = expandedRows.has(channel.channel_id);
              
              return (
                <Card key={channel.channel_id} className="mb-3 border-0 shadow-sm">
                  <Card.Body 
                    className="p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRowExpansion(channel.channel_id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <Badge bg={getStatusVariant(channel.is_active)} className="me-1">
                            {channel.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge bg="info" className="me-1">
                            {channel.token_bindings?.length || 0} bindings
                          </Badge>
                        </div>
                        
                        <div className="fw-medium font-monospace small">{channel.channel_id}</div>
                        <div className="text-muted small">{channel.description || 'No description'}</div>
                        
                        {channel.token_bindings && channel.token_bindings.length > 0 && (
                          <div className="mt-2">
                            <div className="text-muted small">Token Bindings:</div>
                            {channel.token_bindings.map((binding, index) => (
                              <div key={index} className="d-flex align-items-center gap-1 mt-1">
                                <Badge bg="primary" className="small">
                                  Token #{binding.token_id}
                                </Badge>
                                <Badge bg="secondary" className="small">
                                  Prio: {binding.priority}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="text-muted small mt-2">
                          Updated: {formatDate(channel.updated_at)}
                        </div>
                      </div>
                      <div>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(channel);
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBindClick(channel);
                            }}
                          >
                            <Link size={14} />
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAutoBindClick(channel);
                            }}
                            disabled={binding}
                          >
                            <Activity size={14} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(channel);
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                        
                        {channel.token_bindings && channel.token_bindings.length > 0 && (
                          <div className="mt-3">
                            <div className="text-muted small mb-2">Unbind tokens:</div>
                            <div className="d-flex flex-wrap gap-1">
                              {channel.token_bindings.map((binding, index) => (
                                <Button
                                  key={index}
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnbindClick(channel.channel_id, binding.token_id);
                                  }}
                                  disabled={binding}
                                  className="small"
                                >
                                  <Unlink size={12} className="me-1" />
                                  Token #{binding.token_id}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {filteredChannels.length === 0 && (
            <div className="text-center py-5 text-muted">
              No channels found. {searchTerm ? 'Try a different search term.' : 'Add your first channel!'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            {editingChannel ? (
              <>
                <Edit size={20} className="me-2 text-primary" />
                Edit Channel
              </>
            ) : (
              <>
                <Plus size={20} className="me-2 text-primary" />
                Add New Channel
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Channel ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Discord channel ID"
                    value={formData.channel_id}
                    onChange={(e) => handleInputChange('channel_id', e.target.value)}
                    className="py-2"
                    required
                    disabled={!!editingChannel}
                  />
                  <Form.Text className="text-muted">
                    The Discord channel ID where reactions will be performed
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Optional description for this channel"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="py-2"
                  />
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Status</Form.Label>
                  <Form.Select 
                    value={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                    className="py-2"
                  >
                    <option value={true}>✅ Active</option>
                    <option value={false}>❌ Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowModal(false)} 
              disabled={submitting}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingChannel ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingChannel ? 'Update Channel' : 'Add Channel'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Bind Token Modal */}
      <Modal show={showBindModal} onHide={() => setShowBindModal(false)} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            <Link size={20} className="me-2 text-primary" />
            Bind Token to Channel
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleBindSubmit}>
          <Modal.Body className="pt-0">
            {selectedChannel && (
              <Alert variant="info" className="small">
                Channel: <code>{selectedChannel.channel_id}</code>
              </Alert>
            )}
            
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Select Token</Form.Label>
                  {loadingTokens ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <div className="text-muted small mt-2">Loading tokens...</div>
                    </div>
                  ) : (
                    <Form.Select 
                      value={bindFormData.token_id}
                      onChange={(e) => handleBindInputChange('token_id', e.target.value)}
                      className="py-2"
                      required
                    >
                      <option value="">Choose a token...</option>
                      {tokens.map((token) => (
                        <option key={token.id} value={token.id}>
                          {token.label || 'Unlabeled'} ({token.token.slice(0, 8)}...)
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Priority</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="100"
                    value={bindFormData.priority}
                    onChange={(e) => handleBindInputChange('priority', parseInt(e.target.value) || 10)}
                    className="py-2"
                  />
                  <Form.Text className="text-muted">
                    Lower numbers = higher priority (1-100)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowBindModal(false)} 
              disabled={binding}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={binding || !bindFormData.token_id}
              className="px-4"
            >
              {binding ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Binding...
                </>
              ) : (
                'Bind Token'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-danger">
            <AlertTriangle size={24} className="me-2" />
            Delete Channel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-3">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
              <Trash2 size={32} className="text-danger" />
            </div>
            <h5 className="fw-bold">Are you sure you want to delete this channel?</h5>
            {channelToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="fw-medium font-monospace">{channelToDelete.channel_id}</div>
                <div className="text-muted small">{channelToDelete.description || 'No description'}</div>
                <div className="mt-2">
                  <Badge bg={getStatusVariant(channelToDelete.is_active)} className="me-1">
                    {channelToDelete.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge bg="info" className="me-1">
                    {channelToDelete.token_bindings?.length || 0} bindings
                  </Badge>
                </div>
              </div>
            )}
            <p className="text-muted mt-3">
              This action cannot be undone. The channel will be permanently removed from the system.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={handleDeleteCancel}
            className="px-4"
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            className="px-4"
          >
            <Trash2 size={16} className="me-2" />
            Delete Channel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Channels;